import { formatMoneyAmount, getCurrency } from './locale';

export const APPAREL_TYPES = [
  { slug: 'headwear', label: 'Headwear', keywords: ['cap', 'hat', 'beanie', 'headwear', 'visor'] },
  { slug: 'sweaters', label: 'Sweaters', keywords: ['hoodie', 'sweatshirt', 'sweater', 'knit', 'crewneck'] },
  { slug: 'shirts', label: 'Shirts', keywords: ['tee', 't-shirt', 'shirt', 'jersey', 'polo'] },
  { slug: 'shorts-pants', label: 'Shorts & Pants', keywords: ['short', 'pant', 'trouser', 'jogger', 'cargo'] },
  { slug: 'footwear', label: 'Footwear', keywords: ['shoe', 'sneaker', 'boot', 'footwear', 'trainer'] },
  { slug: 'accessories', label: 'Accessories', keywords: ['sock', 'scarf', 'bag', 'glove', 'accessory', 'keychain', 'lanyard', 'watch'] },
];

export const GENDERS = [
  { slug: 'men', label: 'Men' },
  { slug: 'women', label: 'Women' },
  { slug: 'unisex', label: 'Unisex' },
];

export const FILTER_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

/** Base-currency (USD) bounds used for filtering; labels are localized separately. */
export const PRICE_PRESETS = [
  { slug: '0-50', label: 'Under 50', min: 0, max: 50 },
  { slug: '50-100', label: '50 – 100', min: 50, max: 100 },
  { slug: '100-200', label: '100 – 200', min: 100, max: 200 },
  { slug: '200+', label: '200+', min: 200, max: null },
];

export function formatFilterAmount(amount, currencyCode = 'USD') {
  if (amount == null || amount === '') return '';
  return formatMoneyAmount(amount, currencyCode);
}

export function getLocalizedPricePresets(currencyCode = 'USD') {
  return PRICE_PRESETS.map(preset => {
    if (preset.max == null) {
      return {
        ...preset,
        label: `${formatFilterAmount(preset.min, currencyCode)}+`,
      };
    }
    if (preset.min === 0) {
      return {
        ...preset,
        label: `Under ${formatFilterAmount(preset.max, currencyCode)}`,
      };
    }
    return {
      ...preset,
      label: `${formatFilterAmount(preset.min, currencyCode)} – ${formatFilterAmount(
        preset.max,
        currencyCode
      )}`,
    };
  });
}

/** Convert a display-currency amount typed in the filter UI back to base USD. */
export function displayAmountToBase(displayAmount, currencyCode = 'USD') {
  if (displayAmount === '' || displayAmount == null) return '';
  const numeric = Number(displayAmount);
  if (Number.isNaN(numeric)) return '';
  const rate = getCurrency(currencyCode).rate || 1;
  const base = numeric / rate;
  return String(Math.round(base * 100) / 100);
}

/** Convert a base USD filter bound into a display-currency input value. */
export function baseAmountToDisplay(baseAmount, currencyCode = 'USD') {
  if (baseAmount === '' || baseAmount == null) return '';
  const numeric = Number(baseAmount);
  if (Number.isNaN(numeric)) return '';
  const currency = getCurrency(currencyCode);
  const converted = numeric * (currency.rate || 1);
  if (currency.code === 'JPY' || currency.code === 'NGN') {
    return String(Math.round(converted));
  }
  return String(Math.round(converted * 100) / 100);
}

export function getProductPrice(product) {
  const sale = product?.sale_price;
  if (sale != null && sale !== '' && !Number.isNaN(Number(sale))) {
    return Number(sale);
  }
  return Number(product?.price) || 0;
}

function matchesKeywords(text, keywords) {
  return keywords.some(keyword => text.includes(keyword));
}

export function getApparelType(product) {
  const explicit = String(product?.apparel_type || '').trim().toLowerCase();
  if (explicit && APPAREL_TYPES.some(type => type.slug === explicit)) {
    return explicit;
  }

  const haystack = [product?.name, product?.tags, product?.description]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const matched = APPAREL_TYPES.find(type => matchesKeywords(haystack, type.keywords));
  return matched?.slug || '';
}

export function getGender(product) {
  const explicit = String(product?.gender || '').trim().toLowerCase();
  if (explicit && GENDERS.some(item => item.slug === explicit)) {
    return explicit;
  }

  const haystack = [product?.name, product?.tags, product?.description]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (/\b(women|womens|woman|ladies|female)\b/.test(haystack)) return 'women';
  if (/\b(men|mens|man|male)\b/.test(haystack)) return 'men';
  return 'unisex';
}

export function getAvailableSizeLabels(product) {
  const sizes = product?.product_sizes || product?.sizes || [];
  if (!sizes.length) return [];

  return sizes
    .filter(size => size.enabled !== false && Number(size.stock_quantity) > 0)
    .map(size => size.size_label);
}

export function productMatchesFilters(product, filters) {
  if (!product) return false;

  if (filters.apparel) {
    if (getApparelType(product) !== filters.apparel) return false;
  }

  if (filters.gender) {
    const gender = getGender(product);
    if (filters.gender === 'unisex') {
      if (gender !== 'unisex') return false;
    } else if (gender !== filters.gender && gender !== 'unisex') {
      return false;
    }
  }

  if (filters.size) {
    const available = getAvailableSizeLabels(product).map(label => label.toUpperCase());
    if (!available.includes(filters.size.toUpperCase())) return false;
  }

  const price = getProductPrice(product);

  if (filters.minPrice != null && filters.minPrice !== '') {
    if (price < Number(filters.minPrice)) return false;
  }

  if (filters.maxPrice != null && filters.maxPrice !== '') {
    if (price > Number(filters.maxPrice)) return false;
  }

  return true;
}

export function parseFilterParams(searchParams) {
  const min = searchParams.get('min');
  const max = searchParams.get('max');

  return {
    apparel: (searchParams.get('apparel') || '').toLowerCase(),
    gender: (searchParams.get('gender') || '').toLowerCase(),
    size: (searchParams.get('size') || '').toUpperCase(),
    minPrice: min != null && min !== '' ? min : '',
    maxPrice: max != null && max !== '' ? max : '',
  };
}

export function filtersToSearchParams(currentParams, nextFilters) {
  const params = new URLSearchParams(currentParams);

  const setOrDelete = (key, value) => {
    if (value == null || value === '') params.delete(key);
    else params.set(key, value);
  };

  setOrDelete('apparel', nextFilters.apparel || '');
  setOrDelete('gender', nextFilters.gender || '');
  setOrDelete('size', nextFilters.size || '');
  setOrDelete('min', nextFilters.minPrice === '' || nextFilters.minPrice == null ? '' : String(nextFilters.minPrice));
  setOrDelete('max', nextFilters.maxPrice === '' || nextFilters.maxPrice == null ? '' : String(nextFilters.maxPrice));

  return params;
}

export function countActiveFilters(filters) {
  let count = 0;
  if (filters.apparel) count += 1;
  if (filters.gender) count += 1;
  if (filters.size) count += 1;
  if (filters.minPrice !== '' && filters.minPrice != null) count += 1;
  if (filters.maxPrice !== '' && filters.maxPrice != null) count += 1;
  return count;
}
